import { Badge, breakpoints, Card, Inline, Stack, vars } from '@etg/wings';
import { Accordion, CarrierLogo } from '@eti/components';
import { useMemo } from 'react';
import { css, cx } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import type { TravelersType, TripSegmentType } from '../../../models';
import RouteInfo from '../../common/RouteInfo';
import type { CheckInStatusType } from './models';
import {
  badgeInfoPerStatus,
  getPassengersPerCheckInStatus,
  getSegmentCheckInStatus,
  getSegmentCheckInStatuses,
} from './utils';
import AllPassengersSameStatusCheckInInfo from './AllPassengersSameStatusCheckInInfo';
import PartialStatusCheckInInfo from './PartialStatusCheckInInfo';

const borderStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const logoRouteInlineStyles = css`
  @media (min-width: ${breakpoints._560}) {
    align-items: center;
  }
`;

const titleBadgeInlineStyles = css`
  align-items: start;
  flex-flow: wrap;
  flex-direction: column;
  gap: 8px 16px;
  justify-content: space-between;

  @media (min-width: ${breakpoints._560}) {
    flex-direction: row;
  }
`;

const carrierLogoStyles = css`
  height: 24px;
  width: 24px;
`;

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

interface SegmentCheckInInfoProps {
  segment: TripSegmentType;
  checkInStatus: NonNullable<CheckInStatusType>;
  travelers: TravelersType;
  dataTestId?: string;
}

const SegmentCheckInInfo = ({
  segment,
  checkInStatus,
  travelers,
  dataTestId,
}: SegmentCheckInInfoProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();

  const checkInTimeframeInHours = p('OrderDetails.CheckIn.Timeframe.Hours');

  const passengersPerCheckInStatus = useMemo(
    () => getPassengersPerCheckInStatus(checkInStatus, checkInTimeframeInHours, segment, travelers),
    [checkInStatus, checkInTimeframeInHours, segment, travelers],
  );

  const checkInStatuses = useMemo(
    () => getSegmentCheckInStatuses(passengersPerCheckInStatus),
    [passengersPerCheckInStatus],
  );
  const segmentCheckInStatus = useMemo(
    () => getSegmentCheckInStatus(checkInStatuses),
    [checkInStatuses],
  );
  const badgeInfo = badgeInfoPerStatus[segmentCheckInStatus];

  const { segmentId, carrierBookingNumber, origin, destination, arrivedAt, departuredAt } = segment;

  return (
    <Card className={borderStyles} data-testid={dataTestId} role="listitem">
      <Accordion
        id={`check-in-accordion-${segmentId}`}
        isInitiallyExpanded={false}
        spacing={16}
        variant="full-width"
      >
        <Accordion.Header alignY="top">
          <Inline className={titleBadgeInlineStyles} data-testid={`check-in-${segmentId}`}>
            <Inline className={cx(titleBadgeInlineStyles, logoRouteInlineStyles)} noWrap>
              <CarrierLogo
                className={carrierLogoStyles}
                id={carrierBookingNumber?.carrierCode || ''}
                imageAlt={carrierBookingNumber?.carrierName || ''}
              />
              <RouteInfo
                dataTestId={`check-in-route-info-${segmentId}`}
                route={{
                  destination: {
                    cityName: destination.cityName,
                    cityCode: destination.code,
                  },
                  origin: {
                    cityName: origin.cityName,
                    cityCode: origin.code,
                  },
                  arrivedAt,
                  departuredAt,
                }}
              />
            </Inline>
            <Badge variant={badgeInfo.variant}>{t(badgeInfo.text)}</Badge>
          </Inline>
        </Accordion.Header>
        <Accordion.Body>
          <Stack>
            {checkInStatuses.length === 1 ? (
              <Stack data-testid={`check-in-status-${checkInStatuses[0]}`}>
                <div className={dividerStyles} />
                <AllPassengersSameStatusCheckInInfo
                  checkInStatus={checkInStatuses[0]}
                  segment={segment}
                />
              </Stack>
            ) : (
              checkInStatuses.map((status) => {
                return (
                  <Stack data-testid={`partial-${status}-check-in-info`} key={status}>
                    <div className={dividerStyles} />
                    <PartialStatusCheckInInfo
                      checkInStatus={status}
                      segment={segment}
                      travelers={passengersPerCheckInStatus[status]}
                    />
                  </Stack>
                );
              })
            )}
          </Stack>
        </Accordion.Body>
      </Accordion>
    </Card>
  );
};

export default SegmentCheckInInfo;
